import React, { useEffect, useState } from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import QRCode from 'qrcode';

// ─── Brand tokens ────────────────────────────────────────────────────────────
const BRAND   = '#1a3a6b';   // deep navy
const ACCENT  = '#1a73e8';   // brand blue
const GOLD    = '#c9a84c';   // premium gold accent
const LIGHT   = '#f0f4ff';   // header cell bg
const BORDER  = '#d0d8e8';
const MUTED   = '#64748b';
const WHITE   = '#ffffff';
const TEXT    = '#1e293b';

const S = StyleSheet.create({
  page: {
    backgroundColor: WHITE,
    paddingTop: 0,
    paddingBottom: 24,
    paddingHorizontal: 0,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: TEXT,
  },

  // ── Watermark ──
  watermark: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    width: '100%', height: '100%', opacity: 0.04,
  },

  // ── Header band ──
  headerBand: {
    backgroundColor: BRAND,
    paddingVertical: 18,
    paddingHorizontal: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: { width: 72, height: 36, objectFit: 'contain' },
  headerRight: { alignItems: 'flex-end' },
  companyName: {
    fontSize: 15, fontFamily: 'Helvetica-Bold',
    color: WHITE, letterSpacing: 1.2,
  },
  seasonBadge: {
    marginTop: 4, backgroundColor: GOLD,
    paddingHorizontal: 8, paddingVertical: 3,
    borderRadius: 3,
  },
  seasonText: {
    fontSize: 8, fontFamily: 'Helvetica-Bold',
    color: BRAND, letterSpacing: 0.8,
  },

  // ── Gold divider ──
  goldBar: { height: 3, backgroundColor: GOLD },

  // ── Body padding ──
  body: { paddingHorizontal: 28, paddingTop: 16 },

  // ── Reps row ──
  repsRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    backgroundColor: LIGHT, borderRadius: 4,
    paddingVertical: 8, paddingHorizontal: 12,
    marginBottom: 14, borderLeftWidth: 3, borderLeftColor: ACCENT,
  },
  repItem: { flex: 1, paddingRight: 8 },
  repLabel: { fontSize: 7, color: MUTED, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  repValue: { fontSize: 7.5, color: TEXT, fontFamily: 'Helvetica-Bold', lineHeight: 1.5 },

  // ── Info + QR row ──
  infoQrRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    marginBottom: 14, alignItems: 'flex-start',
  },
  infoGrid: { flex: 1, marginRight: 16 },
  infoRow: {
    flexDirection: 'row', marginBottom: 6,
    borderBottomWidth: 1, borderBottomColor: '#e8edf5',
    paddingBottom: 5,
  },
  infoLabel: {
    width: 110, fontSize: 8, color: MUTED,
    fontFamily: 'Helvetica-Bold', textTransform: 'uppercase',
  },
  infoValue: { flex: 1, fontSize: 9, color: TEXT, fontFamily: 'Helvetica-Bold' },
  qrBox: {
    width: 80, alignItems: 'center',
  },
  qrImage: { width: 72, height: 72 },
  qrLabel: { fontSize: 6.5, color: MUTED, marginTop: 4, textAlign: 'center' },

  // ── Section label ──
  sectionLabel: {
    fontSize: 7.5, fontFamily: 'Helvetica-Bold',
    color: ACCENT, textTransform: 'uppercase',
    letterSpacing: 0.8, marginBottom: 4, marginTop: 10,
  },

  // ── Table ──
  table: {
    borderWidth: 1, borderColor: BORDER,
    borderRadius: 3, overflow: 'hidden', marginBottom: 2,
  },
  tRow: { flexDirection: 'row' },
  tHead: {
    backgroundColor: BRAND, paddingVertical: 5,
    paddingHorizontal: 6, flex: 1, textAlign: 'center',
    fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: WHITE,
    borderRightWidth: 1, borderRightColor: 'rgba(255,255,255,0.15)',
  },
  tCell: {
    paddingVertical: 5, paddingHorizontal: 6,
    flex: 1, textAlign: 'center', fontSize: 8,
    borderRightWidth: 1, borderRightColor: BORDER,
    borderTopWidth: 1, borderTopColor: BORDER,
    color: TEXT,
  },
  tCellAlt: { backgroundColor: '#f8faff' },

  // ── Transport row ──
  transportRow: {
    flexDirection: 'row', backgroundColor: LIGHT,
    borderRadius: 3, padding: 8, marginBottom: 2,
    borderLeftWidth: 3, borderLeftColor: GOLD,
    alignItems: 'center',
  },
  transportLabel: {
    fontSize: 8, fontFamily: 'Helvetica-Bold',
    color: MUTED, width: 110,
  },
  transportValue: {
    fontSize: 9, fontFamily: 'Helvetica-Bold', color: TEXT,
  },

  // ── Terms ──
  termsBox: {
    marginTop: 12, borderTopWidth: 1, borderTopColor: BORDER,
    paddingTop: 8,
  },
  termsTitle: {
    fontSize: 7.5, fontFamily: 'Helvetica-Bold',
    color: BRAND, marginBottom: 5, textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  termItem: {
    flexDirection: 'row', marginBottom: 3,
  },
  bullet: { fontSize: 8, color: GOLD, marginRight: 5, marginTop: 0.5 },
  termText: { fontSize: 7.5, color: MUTED, flex: 1, lineHeight: 1.4 },

  // ── Footer ──
  footer: {
    marginTop: 14, paddingTop: 8,
    borderTopWidth: 2, borderTopColor: GOLD,
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: { fontSize: 7, color: MUTED },
  validBadge: {
    backgroundColor: BRAND, paddingHorizontal: 10,
    paddingVertical: 4, borderRadius: 3,
  },
  validText: {
    fontSize: 7.5, color: WHITE,
    fontFamily: 'Helvetica-Bold', letterSpacing: 0.5,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const generateQRCodeImage = async (text) => {
  try {
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, text, { width: 200, margin: 1 });
    return canvas.toDataURL();
  } catch {
    return null;
  }
};

const InfoRow = ({ label, value }) => (
  <View style={S.infoRow}>
    <Text style={S.infoLabel}>{label}</Text>
    <Text style={S.infoValue}>{value || '—'}</Text>
  </View>
);

const TableSection = ({ title, rows }) => (
  <View>
    <Text style={S.sectionLabel}>{title}</Text>
    <View style={S.table}>
      <View style={S.tRow}>
        {rows.headers.map((h, i) => (
          <Text key={i} style={S.tHead}>{h}</Text>
        ))}
      </View>
      {rows.data.map((row, ri) => (
        <View key={ri} style={S.tRow}>
          {row.map((cell, ci) => (
            <Text key={ci} style={[S.tCell, ri % 2 === 1 && S.tCellAlt]}>{cell || '—'}</Text>
          ))}
        </View>
      ))}
    </View>
  </View>
);

// ─── Component ───────────────────────────────────────────────────────────────
const PdfFile = ({ data }) => {
  const [qrUrl, setQrUrl] = useState('');
  const userLocal = localStorage.getItem('userId');

  useEffect(() => {
    generateQRCodeImage(
      `https://voucher.hamsyltravels.com/search/print/${data.search_id}`
    ).then(url => setQrUrl(url || ''));
  }, [data]);

  const passengerCount = (data && data.multipleReg && data.multipleReg.length) || 0;

  return (
    <Document>
      <Page size="A4" style={S.page}>

    
        {/* ── Header ── */}
        <View style={S.headerBand}>
          <Image style={S.logo} src={require('../../../../assets/images/LOGO25.png')} />
          <View style={S.headerRight}>
            <Text style={S.companyName}>HAMSYL TRAVEL AND TOURS LTD</Text>
            <View style={S.seasonBadge}>
              <Text style={S.seasonText}>  UMRAH SEASON 2026 / 2027  </Text>
            </View>
          </View>
        </View>
        <View style={S.goldBar} />

        <View style={S.body}>

          {/* ── Representatives ── */}
          <View style={S.repsRow}>
            <View style={S.repItem}>
              <Text style={S.repLabel}>Jeddah Airport Rep</Text>
              <Text style={S.repValue}>BAITA{'\n'}(+966) 564 293 357</Text>
            </View>
            <View style={S.repItem}>
              <Text style={S.repLabel}>Madina Representative</Text>
              <Text style={S.repValue}>(+966) 545 636 743{'\n'}(+966) 500 253 118{'\n'}</Text>
            </View>
            <View style={S.repItem}>
              <Text style={S.repLabel}>Makkah Representative</Text>
              <Text style={S.repValue}>(+966) 562 911 308 {'\n'} (+966) 594 790 653</Text>
            </View>
          </View>

          {/* ── Customer info + QR ── */}
          <View style={S.infoQrRow}>
            <View style={S.infoGrid}>
              <InfoRow label="Customer Name"  value={data && data.agentName} />
              <InfoRow label="Saudi Company"  value={data && data.saudiCompanies} />
              <InfoRow label="Airlines"       value={data && data.airline} />
              <InfoRow label="Voucher ID"     value={data && data.voucherId} />
              <InfoRow label="User ID"        value={(data && data.user && data.user.userId) || userLocal} />
              <InfoRow label="Passengers"     value={String(passengerCount)} />
            </View>
            <View style={S.qrBox}>
              {qrUrl
                ? <Image style={S.qrImage} src={qrUrl} />
                : <Text style={{ fontSize: 7, color: 'red' }}>QR unavailable</Text>
              }
              <Text style={S.qrLabel}>Scan to verify voucher</Text>
            </View>
          </View>

          {/* ── Passenger table ── */}
          <TableSection
            title="Passenger Details"
            rows={{
              headers: ['S/N', 'Reg ID', 'Passenger Name', 'Passport No.'],
              data: data && data.multipleReg && data.multipleReg.length
                ? data.multipleReg.map((p, i) => [
                    String(i + 1),
                    data.voucherId,
                    p.name,
                    p.passport,
                  ])
                : [['—', '—', 'No passengers', '—']],
            }}
          />

          {/* ── Madina accommodation ── */}
          <TableSection
            title="Madina Accommodation"
            rows={{
              headers: ['City', 'Nights', 'Room Type', 'Hotel', 'Check-in', 'Check-out'],
              data: [[
                data && data.cityMadina,
                data && data.nightsMadina,
                data && data.roomTypeMadina,
                data && data.hotelMadina,
                data && data.checkInMadina,
                data && data.checkOutMadina,
              ]],
            }}
          />

          {/* ── Makkah accommodation ── */}
          <TableSection
            title="Makkah Accommodation"
            rows={{
              headers: ['City', 'Nights', 'Room Type', 'Hotel', 'Check-in', 'Check-out'],
              data: [[
                data && data.cityMakkah,
                data && data.nightsMakkah,
                data && data.roomTypeMakkah,
                data && data.hotelMakkah,
                data && data.checkInMakkah,
                data && data.checkOutMakkah,
              ]],
            }}
          />

          {/* ── Transportation ── */}
          <Text style={S.sectionLabel}>Transportation</Text>
          <View style={S.transportRow}>
            <Text style={S.transportLabel}>Type</Text>
            <Text style={S.transportValue}>{(data && data.transportationType) || '—'}</Text>
          </View>

          {/* ── Terms ── */}
          <View style={S.termsBox}>
            <Text style={S.termsTitle}>Important Terms &amp; Conditions</Text>
            {[
              'Download the NUSUK app to issue permits for Rawdah and the Prophet\'s Mosque.',
              'Transportation is provided for groups only. Individual arrivals are responsible for their own transport.',
              'Package is non-refundable under any circumstances.',
              'Maximum 4 days allowed in Madina. Check-out time is 12:00 PM.',
              'Check-in time is 4:00 PM. Early arrivals are subject to waiting or availability.',
              'Accommodation shall be in a hotel or approved building as per Saudi competent authority standards.',
              'I confirm that the above package has been chosen by me.',
            ].map((t, i) => (
              <View key={i} style={S.termItem}>
                <Text style={S.bullet}>•</Text>
                <Text style={S.termText}>{t}</Text>
              </View>
            ))}
          </View>

          {/* ── Footer ── */}
          <View style={S.footer}>
            <Text style={S.footerText}>www.hamsyltravels.com  ·  Generated by Hamsyl Travel System</Text>
            <View style={S.validBadge}>
              <Text style={S.validText}>VALID: UMRAH 2026/2027</Text>
            </View>
          </View>

        </View>
      </Page>
    </Document>
  );
};

export default PdfFile;
